import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ResourceApplyResponse,
  ResourceChangeGrant,
  ResourceDetailResponse,
  ResourceOperationResponse,
} from "@k8s-dashboard/contracts";
import {
  CHANGE_UNKNOWN_MESSAGE,
  changeErrorMessage,
  fetchOperation,
  isUnknownResult,
  OPERATION_STATE_TEXT,
  OPERATION_UNKNOWN_STATE_TEXT,
  newCorrelationId,
  requestIdOf,
  submitApply,
} from "./change";
import { fixedTextFor } from "./dryrun";

/**
 * 검토 → 확인 → 적용 (ADR 0019 Phase 2)
 * --------------------------------------------------------------------------
 * 검토가 자격(grant)을 준 경우에만 나타납니다. 화면이 자격을 추론하지 않습니다 —
 * 서버가 준 것이 없으면 이 단계 자체가 없습니다.
 *
 * 세 가지가 이 컴포넌트의 계약입니다.
 *
 *  1. **자격은 어디에도 저장하지 않습니다.** props로 받아 요청 본문에 한 번 싣고
 *     버립니다. URL·저장소·로그·DOM 속성 어디에도 쓰지 않습니다.
 *  2. **제출 이후에는 취소 버튼이 없습니다.** 누르기 전까지만 되돌릴 수 있습니다.
 *     제출한 뒤 언마운트되어도 요청은 끊기지 않습니다(change.ts에 signal 없음).
 *  3. **결과를 모르면 "취소됨"이라고 말하지 않습니다.** requestId로 작업 상태를
 *     다시 물어보는 버튼을 주고, 그때까지는 결과 불명이라고 정직하게 씁니다.
 */

type Phase = "idle" | "submitting" | "applied" | "error" | "unknown";

export interface ApplyStepProps {
  detail: ResourceDetailResponse;
  grant: ResourceChangeGrant;
  /** 적용이 끝나면 상위가 목록·상세를 다시 조회합니다. */
  onApplied: (result: ResourceApplyResponse) => void;
}

export function ApplyStep({ detail, grant, onApplied }: ApplyStepProps) {
  const [confirmName, setConfirmName] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [operation, setOperation] = useState<ResourceOperationResponse | undefined>(undefined);
  const [checking, setChecking] = useState(false);

  /* 언마운트 뒤 상태를 건드리지 않기 위한 표식입니다. 제출한 요청 자체는
     끊지 않습니다 — 끊으면 서버가 적용했는데 화면은 취소로 보입니다. */
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  /* 대상이나 자격이 바뀌면 확인 입력과 결과를 되돌립니다. */
  useEffect(() => {
    setConfirmName("");
    setPhase("idle");
    setMessage(undefined);
    setRequestId(undefined);
    setOperation(undefined);
  }, [grant.token, detail.uid, detail.resourceVersion]);

  const matches = confirmName === detail.name;

  const onApply = async () => {
    if (!matches || phase === "submitting") return;
    setPhase("submitting");
    setMessage(undefined);
    setOperation(undefined);
    /* 상관 ID는 **보내기 전에** 만듭니다. 응답에서 받은 값에만 의존하면 진짜
       네트워크 단절에서 물어볼 ID가 없습니다 — 그때가 결과를 가장 알고 싶은
       순간입니다. 서버는 이 값을 그대로 재사용하고 응답·감사가 같은 값을 씁니다. */
    const correlationId = newCorrelationId();
    setRequestId(correlationId);
    try {
      const result = await submitApply({ detail, token: grant.token, confirmName, requestId: correlationId });
      if (!aliveRef.current) return;
      setRequestId(result.requestId || correlationId);
      setPhase("applied");
      onApplied(result);
    } catch (error) {
      if (!aliveRef.current) return;
      // 응답이 있으면 서버 값을, 없으면(진짜 단절) 미리 만든 값을 씁니다.
      setRequestId(requestIdOf(error) ?? correlationId);
      if (isUnknownResult(error)) {
        /* 서버가 이미 적용했을 수 있습니다. 실패로 표시하지 않습니다. */
        setMessage(CHANGE_UNKNOWN_MESSAGE);
        setPhase("unknown");
        return;
      }
      setMessage(changeErrorMessage(error));
      setPhase("error");
    }
  };

  const onCheck = useCallback(async () => {
    if (!requestId || checking) return;
    setChecking(true);
    try {
      const status = await fetchOperation({ clusterId: detail.clusterId, requestId });
      if (aliveRef.current) setOperation(status);
    } catch (error) {
      if (aliveRef.current) setMessage(changeErrorMessage(error));
    } finally {
      if (aliveRef.current) setChecking(false);
    }
  }, [detail.clusterId, requestId, checking]);

  return (
    <section className="resource-apply" aria-label="변경 적용">
      <p className="resource-apply__warning" role="note">
        <span className="resource-review__glyph" aria-hidden="true">
          !
        </span>
        위 검토 결과를 <strong>실제로 적용</strong>합니다. 제출한 뒤에는 취소할 수 없습니다.
        자격은 {grant.ttlSeconds / 60}분 뒤 만료되며 한 번만 사용됩니다.
      </p>

      <label className="resource-apply__confirm" htmlFor="resource-apply-confirm">
        <span className="resource-review__label">
          적용하려면 대상 이름 <code className="ds-ident">{detail.name}</code>을(를) 입력하세요
        </span>
        <input
          id="resource-apply-confirm"
          type="text"
          className="resource-apply__input"
          value={confirmName}
          autoComplete="off"
          spellCheck={false}
          maxLength={253}
          disabled={phase === "submitting" || phase === "applied"}
          onChange={(e) => setConfirmName(e.target.value)}
        />
      </label>

      <div className="resource-review__actions">
        <button
          type="button"
          className="ds-button ds-button--sm ds-button--destructive"
          disabled={!matches || phase === "submitting" || phase === "applied"}
          onClick={() => void onApply()}
        >
          {phase === "submitting" ? "적용하는 중…" : "변경 적용"}
        </button>
        {phase === "submitting" && (
          <span className="muted" role="status">
            제출했습니다. 이 요청은 취소할 수 없습니다.
          </span>
        )}
      </div>

      {phase === "applied" && (
        <div className="state state--applied" role="status">
          <span className="state__glyph" aria-hidden="true">
            ✓
          </span>
          <span className="state__title">적용했습니다</span>
          <span className="state__detail">최신 상태를 다시 불러왔습니다.</span>
        </div>
      )}

      {(phase === "error" || phase === "unknown") && (
        <div className={phase === "unknown" ? "state state--forbidden" : "state state--error"} role="alert">
          <span className="state__glyph" aria-hidden="true">
            {phase === "unknown" ? "?" : "✕"}
          </span>
          <span className="state__title">
            {phase === "unknown" ? "결과를 확인하지 못했습니다" : "적용하지 못했습니다"}
          </span>
          <span className="state__detail">{message}</span>
          {requestId && (
            <span className="resource-apply__request muted">
              요청 ID <span className="ds-ident">{requestId}</span>
            </span>
          )}
        </div>
      )}

      {phase === "unknown" && requestId && (
        <div className="resource-review__actions">
          <button type="button" className="ds-button ds-button--sm" disabled={checking} onClick={() => void onCheck()}>
            {checking ? "확인하는 중…" : "작업 상태 확인"}
          </button>
          {operation && (
            <span className="resource-apply__operation" role="status">
              {fixedTextFor(OPERATION_STATE_TEXT, operation.state) ?? OPERATION_UNKNOWN_STATE_TEXT}
            </span>
          )}
        </div>
      )}
    </section>
  );
}
